import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import '@/index.css';
import { initAnalytics, identifyUser, resetAnalytics, capturePageview, startSessionRecording, stopSessionRecording } from '@/lib/analytics';
import Landing from './pages/Landing';
import PublicOpportunities from './pages/PublicOpportunities';
import Login from './pages/Login';
import ClaimStartup from './pages/ClaimStartup';
import PreDashboard from './pages/PreDashboard';
import OAuthCallback from './pages/OAuthCallback';
import Welcome from './pages/Welcome';
import VerifyEmail from './pages/VerifyEmail';
import ResetPassword from './pages/ResetPassword';
import VCSignup from './pages/VCSignup';
import StartupSignup from './pages/StartupSignup';
import VCApp from './pages/vc/VCApp';
import StartupAppNew from './pages/startup/StartupAppNew';
import AcceptInvite from './pages/AcceptInvite';
import AcceptTeamInvite from './pages/AcceptTeamInvite';
import PublicApply from './pages/PublicApply';
import ProgramApplicationConfirmation from './pages/ProgramApplicationConfirmation';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import StudioAdmin from './pages/StudioAdmin';
import StudioLogin from './pages/StudioLogin';
import StudioAcceptInvite from './pages/StudioAcceptInvite';
import AgencyApply from './pages/AgencyApply';
import AgencyApp from './pages/AgencyApp';
import AgencyAcceptInvite from './pages/AgencyAcceptInvite';
import VendorCenter from './pages/VendorCenter';
import VendorApplyForm from './pages/VendorApplyForm';
import VendorApp from './pages/VendorApp';
import ServicesLogin from './pages/ServicesLogin';
import CreditLogin from './pages/CreditLogin';
import VendorSetupPassword from './pages/VendorSetupPassword';
import VaultShareLanding from './pages/VaultShareLanding';
import AdminApp from './pages/admin/AdminApp';
import AdminLogin from './pages/admin/AdminLogin';
import AdminSignup from './pages/admin/AdminSignup';
import ErrorBoundary from './components/ErrorBoundary';
import { CurrencyProvider } from '@/hooks/useCurrency';
import { ModuleAccessProvider } from '@/moduleAccess';
import { Toaster } from 'sonner';

let __apiInterceptorInstalled = false;
const install5xxInterceptor = () => {
  if (__apiInterceptorInstalled) return;
  __apiInterceptorInstalled = true;
  api.interceptors.response.use(
    (r) => r,
    (err) => {
      // Effect cleanup and route changes abort requests intentionally. Axios
      // cancellations have no response, but they are not offline/server failures.
      if (err?.code === 'ERR_CANCELED' || err?.__CANCEL__ === true || err?.name === 'AbortError') {
        return Promise.reject(err);
      }
      const status = err?.response?.status;
      if (!err?.response || (status >= 500 && status < 600)) {
        const skip = err?.config?.headers?.['X-Suppress-Error-Toast'];
        if (!skip) {
          const detail = err?.response?.data?.detail;
          toast.error(
            typeof detail === 'string' && detail
              ? detail
              : `Server hiccup (${status || 'offline'}) — please try again in a moment.`
          );
        }
      }
      return Promise.reject(err);
    }
  );
};
install5xxInterceptor();
initAnalytics();

// Authenticated app shells that can render startup financials (MRR, runway,
// cash reserves) or vault documents — session recording/heatmaps must never
// run here. Everything outside this list (landing, /apply application
// funnel, login/signup, vendor/studio apply forms, etc.) is public-facing
// and safe to record.
const SENSITIVE_ROUTE_PREFIXES = [
  '/claim-startup','/vc', '/startup', '/agency', '/vendor', '/admin', '/studio/admin', '/vault/share', '/pre-dashboard'];
const isSensitiveRoute = (pathname) => SENSITIVE_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

// Renders inside <BrowserRouter> so useLocation is available — PostHog's
// capture_pageview only fires on a hard browser navigation, not React
// Router's client-side route changes, so those are captured by hand here.
// Session recording is likewise started/stopped per-route: see
// SENSITIVE_ROUTE_PREFIXES and the comment in lib/analytics.js.
const PostHogPageviewTracker = () => {
  const location = useLocation();
  useEffect(() => {
    capturePageview(location.pathname + location.search);
    if (isSensitiveRoute(location.pathname)) {
      stopSessionRecording();
    } else {
      startSessionRecording();
    }
  }, [location.pathname, location.search]);
  return null;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Distinct id per user — identify once we know who they are, reset on
  // logout (setUser(null)) so the next session on this device/browser
  // doesn't inherit the previous person's identity. Deliberately keyed on
  // user?.id rather than the whole user object — re-identifying on every
  // object-reference change (e.g. a cache-merge re-render with the same id)
  // would be wasted, and identifyUser reads whatever `user` currently holds.
  useEffect(() => {
    if (loading) return; // still resolving /auth/me — wait for a definitive answer
    if (user?.id) identifyUser(user);
    else resetAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, loading]);

  useEffect(() => {
    // Logged-out / pre-auth pages are always light mode. Do NOT overwrite the
    // saved preference here — it is restored once authentication succeeds.
    document.documentElement.setAttribute('data-theme', 'light');

    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    api.get('/auth/me')
      .then(res => {
        const cachedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const merged = { ...cachedUser, ...res.data };
        localStorage.setItem('user', JSON.stringify(merged));
        setUser(merged);
      })
      .catch(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loading) return;

    if (user && user.onboarding_complete) {
      // Only restore the user's saved app theme once onboarding is complete.
      // The signup/onboarding forms remain part of the pre-app experience and
      // are intentionally light mode only.
      const savedTheme = localStorage.getItem('theme') || 'light';
      document.documentElement.setAttribute('data-theme', savedTheme);
    } else {
      // Logged-out pages AND signup/onboarding pages stay light. Keep the saved
      // preference untouched so it can be restored after onboarding/login.
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, [user, loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-text-secondary">Loading...</div>
      </div>
    );
  }

  const getDefaultRoute = (u) => {
    if (!u) return null;
    // Vendor accounts (services/credit) → unified vendor dashboard, never the founder app.
    if (u.role === 'vendor' || u.vendor_id) return '/vendor';
    // Dual-role accounts (agency teammates that also have a founder profile) land on the agency shell.
    if (u.agency_id && u.agency_role && u.agency_active !== false) return '/agency';
    if (!u.onboarding_complete) return u.role === 'vc' ? '/signup/vc' : '/signup/startup';
    return u.role === 'vc' ? '/vc/chat' : '/startup/dashboard';
  };

  return (
    <>
      <CurrencyProvider user={user}>
      <ModuleAccessProvider>
      <BrowserRouter>
        <PostHogPageviewTracker />
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={user ? <Navigate to={getDefaultRoute(user)} /> : <Landing />} />
            <Route path="/programs" element={<PublicOpportunities key="programs" kind="programs" />} />
            <Route path="/services" element={<PublicOpportunities key="services" kind="services" />} />
            <Route path="/signup" element={user ? <Navigate to={getDefaultRoute(user)} /> : <Login setUser={setUser} />} />
            <Route path="/login" element={user ? <Navigate to={getDefaultRoute(user)} /> : <Login setUser={setUser} />} />
            <Route
              path="/pre-dashboard"
              element={
                user && user.onboarding_complete
                  ? <PreDashboard user={user} />
                  : user
                    ? <Navigate to={getDefaultRoute(user)} replace />
                    : <Navigate to="/login" replace />
              }
            />
            <Route path="/oauth/callback/:provider" element={<OAuthCallback />} />
            <Route path="/claim-startup/:token" element={<ClaimStartup user={user} />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<Navigate to="/login" replace state={{ forgotPassword: true }} />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/studio/login" element={<StudioLogin onLogin={(u) => setUser(u)} />} />
            <Route path="/studio/apply" element={<AgencyApply />} />
            <Route path="/services/apply" element={<VendorApplyForm mode="service" />} />
            <Route path="/services/login" element={user ? <Navigate to={getDefaultRoute(user)} /> : <ServicesLogin onLogin={(u) => setUser(u)} />} />
            <Route path="/credit/apply" element={<VendorApplyForm mode="credit" />} />
            <Route path="/credit/login" element={user ? <Navigate to={getDefaultRoute(user)} /> : <CreditLogin onLogin={(u) => setUser(u)} />} />
            <Route path="/vendor/setup/:token" element={<VendorSetupPassword onLogin={(u) => setUser(u)} />} />
            <Route path="/vault/share/:token" element={<VaultShareLanding />} />
            <Route path="/vendors" element={<VendorCenter />} />
            <Route path="/vendor/*" element={user ? <VendorApp user={user} setUser={setUser} /> : <Navigate to="/services/login" />} />
            <Route path="/admin/login" element={user && (user.is_super_admin || user.studio_admin) ? <Navigate to="/admin" replace /> : <AdminLogin setUser={setUser} />} />
            <Route path="/admin/signup" element={<AdminSignup />} />
            <Route path="/admin/*" element={user && (user.is_super_admin || user.studio_admin) ? <AdminApp user={user} setUser={setUser} /> : <Navigate to="/admin/login" replace />} />
            <Route path="/studio/accept-invite/:token" element={<StudioAcceptInvite onLogin={(u) => setUser(u)} />} />
            <Route path="/agency/accept-invite/:token" element={<AgencyAcceptInvite onLogin={(u) => setUser(u)} />} />
            <Route path="/studio/admin" element={user && user.studio_admin ? <StudioAdmin user={user} setUser={setUser} /> : <Navigate to="/studio/login" />} />
            <Route path="/signup/vc" element={user && user.role === 'vc' ? <VCSignup user={user} /> : <Navigate to="/" />} />
            <Route path="/signup/startup" element={user && user.role === 'startup' ? <StartupSignup user={user} /> : <Navigate to="/" />} />
            <Route path="/accept-invite/:token" element={<AcceptInvite />} />
            <Route path="/accept-team-invite/:token" element={<AcceptTeamInvite />} />
            <Route path="/apply/:slug" element={<PublicApply />} />
            <Route path="/apply/:slug/confirmation" element={<ProgramApplicationConfirmation />} />
            <Route path="/vc/*" element={user && user.role === 'vc' && user.onboarding_complete ? <VCApp user={user} setUser={setUser} /> : user ? <Navigate to={getDefaultRoute(user)} /> : <Navigate to="/" />} />
            <Route path="/startup/*" element={user && user.role === 'startup' && user.onboarding_complete ? <StartupAppNew user={user} setUser={setUser} /> : user ? <Navigate to={getDefaultRoute(user)} /> : <Navigate to="/" />} />
            <Route path="/agency/*" element={user && user.agency_id && user.agency_role ? <AgencyApp user={user} setUser={setUser} /> : <Navigate to="/studio/login" />} />
          </Routes>
        </ErrorBoundary>
      </BrowserRouter>
      </ModuleAccessProvider>
      </CurrencyProvider>
      <Toaster position="top-right" theme="dark" />
    </>
  );
}

export default App;
